import React, { useState } from "react";
import QRCode from "react-qr-code";
import { Copy } from "iconsax-reactjs";
import { useSelector } from "react-redux";
import SelectNetworkModal from "component/wallet/SelectNetworkModal";
import { copyAllFields, copyField } from "../../utilities/clipboard";
import QRLogo from "asset/icons/logo.svg";

import NGNFlagImg from "asset/images/Currency.svg";
import GBPFlagImg from "asset/images/GB.svg";
import USFlagImg from "asset/images/US.png";
import EUFlagImg from "asset/images/EU.svg";
import USDC from "asset/icons/USDC.svg";
import USDT from "asset/icons/USDT.svg";

const getFlagImg = (currency) => {
    switch ((currency || "").toUpperCase()) {
        case "NGN":
            return NGNFlagImg;
        case "USD":
            return USFlagImg;
        case "EUR":
            return EUFlagImg;
        case "GBP":
            return GBPFlagImg;
        case "USDT":
            return USDT;
        case "USDC":
            return USDC;
        default:
            return NGNFlagImg;
    }
};

const isCrypto = (c) => ["USDC", "USDT"].includes(String(c || "").toUpperCase());

export default function AccountDetailsDrawer({ isOpen, onClose, onAddNew, account = {} }) {
    const { currency = "NGN", accountNumber, bankName, accountName } = account;
    const { business } = useSelector((s) => s);

    const [showNetworkModal, setShowNetworkModal] = useState(false);
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [addrLoading, setAddrLoading] = useState(false);

    if (!isOpen) return null;

    const headerTitle = `${currency} Account details`;

    const ccy = String(currency || "").toUpperCase();
    const isUSD = ccy === "USD";
    const isKES = ccy === "KES";

    const rails = Array.isArray(account?.paymentRails)
        ? account.paymentRails
        : Array.isArray(account?.meta?.deposit_instructions?.payment_rails)
        ? account.meta.deposit_instructions.payment_rails
        : [];

    // Build the exact list of fields to render (and copy)
    const fiatFields = [
        { label: "Account number", value: accountNumber },
        { label: "Bank name", value: bankName },
        { label: "Account name", value: accountName },

        ...(rails.length ? [{ label: "Payment rails", value: rails }] : []),

        ...(isKES && account?.branchName
            ? [{ label: "Branch name", value: account.branchName }]
            : []),

        ...(isUSD
            ? [
                  { label: "Bank address", value: account?.bankAddress },
                  { label: "Bank code", value: account?.bankCode },
                  { label: "Routing number", value: account?.routingNumber },
              ]
            : []),
    ];

    const walletAddress =
        selectedNetwork?.address ||
        account?.accountNumber ||
        account?.walletAddress ||
        account?.depositAddress ||
        "";

    const copyAllFiat = () => copyAllFields(fiatFields);

    const copyAllCrypto = () =>
        copyAllFields({
            Network: selectedNetwork?.label || selectedNetwork?.name || selectedNetwork?.id || "",
            "Wallet address": walletAddress || "",
        });

    return (
        <div>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/25 z-[800]" onClick={onClose} />

            {/* Drawer (stays open) */}
            <div
                className="fixed inset-y-0 right-0 w-full max-w-lg rounded-tl-xl rounded-bl-xl bg-white shadow-lg z-[999] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-3">
                        <img
                            src={getFlagImg(currency)}
                            alt={`${currency} flag`}
                            className="w-6 h-6"
                        />
                        <h2 className="text-xl font-semibold mb-0">{headerTitle}</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 flex flex-col gap-4">
                    {!isCrypto(currency) ? (
                        <>
                            <p className="text-sm">Receive funds with the details below</p>

                            <div className="bg-[#F9FAFF] p-3 rounded-lg space-y-4 flex-1 overflow-auto">
                                {fiatFields.map((f) => {
                                    const isArray = Array.isArray(f.value);
                                    const displayValue = isArray ? f.value : f.value || "";

                                    return (
                                        <div
                                            key={f.label}
                                            className="flex justify-between items-start gap-4"
                                        >
                                            <span className="text-sm text-gray-500 pt-0.5">
                                                {f.label}
                                            </span>

                                            <div className="flex items-start gap-2 justify-end min-w-0">
                                                {/* Value */}
                                                <div className="min-w-0 text-right">
                                                    {isArray ? (
                                                        <div className="flex flex-wrap gap-2 justify-end">
                                                            {displayValue.length ? (
                                                                displayValue.map((r) => (
                                                                    <span
                                                                        key={r}
                                                                        className="px-2 py-1 rounded-full text-xs font-medium bg-[#EEF4FF] text-[#101828] border border-[#D6E4FF]"
                                                                    >
                                                                        {String(r).toUpperCase()}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-sm font-medium text-[#101828]">
                                                                    —
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm font-medium text-[#101828] break-words">
                                                            {displayValue || "—"}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Copy */}
                                                {((isArray && displayValue.length) ||
                                                    (!isArray && displayValue)) && (
                                                    <button
                                                        onClick={() =>
                                                            copyField(
                                                                isArray
                                                                    ? displayValue.join(", ")
                                                                    : displayValue,
                                                                f.label
                                                            )
                                                        }
                                                        className="shrink-0 pt-0.5"
                                                    >
                                                        <Copy size="20" color="#6F739B" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <button
                                onClick={copyAllFiat}
                                className="mt-1 flex items-center gap-2 text-sm text-[#6F00FF]"
                            >
                                <Copy size="20" color="#6F00FF" />
                                Copy all details
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm">Receive funds via a wallet address</p>

                            {/* Selected network / selector */}
                            <div className="bg-[#F7F7FE] rounded-xl !p-4">
                                <p className="text-sm text-[#6B6F86] mb-1">
                                    {selectedNetwork ? "Selected network" : ""}
                                </p>
                                <div className="flex items-center justify-between">
                                    <p className="!text-[18px] !font-[500]">
                                        {selectedNetwork
                                            ? selectedNetwork.name ||
                                              selectedNetwork.label ||
                                              selectedNetwork.id
                                            : "No network selected"}
                                    </p>
                                    <button
                                        className="text-[#7A27FF] font-medium flex items-center gap-1"
                                        onClick={() => setShowNetworkModal(true)}
                                    >
                                        {selectedNetwork ? "Change" : "Select network"}{" "}
                                        <span>›</span>
                                    </button>
                                </div>
                            </div>

                            {/* QR + address after selection; drawer remains open */}
                            {selectedNetwork && (
                                <div className="mt-4">
                                    <div className="flex gap-4 items-start">
                                        <div className="relative bg-white p-2 rounded-xl">
                                            <QRCode
                                                value={walletAddress || ""}
                                                size={160}
                                                fgColor="#6F00FF"
                                                bgColor="#ffffff"
                                                style={{ height: 160, width: 160 }}
                                            />
                                            <img
                                                src={QRLogo}
                                                alt="Logo"
                                                className="absolute top-1/2 left-1/2 w-8 h-8 -translate-x-1/2 -translate-y-1/2 rounded-md"
                                            />
                                        </div>

                                        <div className="flex-1">
                                            <p className="text-sm text-[#6F739B] mb-1">
                                                Wallet address
                                            </p>
                                            <p className="text-[16px] font-[500] text-[#161827] break-all">
                                                {addrLoading ? "Loading…" : walletAddress || "—"}
                                            </p>
                                            <button
                                                className="text-[#6F00FF] mt-2 flex items-center gap-1 text-sm font-medium"
                                                onClick={() =>
                                                    walletAddress &&
                                                    copyField(walletAddress, "Wallet address")
                                                }
                                                disabled={!walletAddress}
                                            >
                                                <Copy size="18" />
                                                Copy
                                            </button>
                                        </div>
                                    </div>

                                    <p className="text-[14px] text-[#383B56] mt-4">
                                        ⚠️ Only send {currency} via the selected network to this
                                        address. Sending through other networks may result in loss.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Network picker (modal closes, drawer stays) */}
            {isCrypto(currency) && (
                <SelectNetworkModal
                    isOpen={showNetworkModal}
                    onClose={() => setShowNetworkModal(false)}
                    onSelect={(net) => {
                        setSelectedNetwork(net || null);
                        setShowNetworkModal(false);
                    }}
                    selectedId={selectedNetwork?.id}
                    currency={currency}
                    businessId={business?._id}
                />
            )}
        </div>
    );
}
